/**
 * Live auth probe — sends an unauthenticated POST to /api/<name> and
 * classifies the response. Runs in the browser; relies on the wildcard
 * Access-Control-Allow-Origin set by the component-server.
 *
 *   verdict "open"          → 2xx with no error envelope (anyone can call)
 *   verdict "blocked"       → 401/403, 2xx-wrapped 4xx, or 5xx with auth-related body
 *   verdict "inconclusive"  → CORS/network failure, timeout, other 4xx/5xx
 *
 * `warnings` may include "wrappedStatus" when a function returns
 * HTTP 200 with a `{ statusCode: 4xx, ... }` body. Clients reading
 * HTTP status (axios validateStatus, fetch().ok) will treat the
 * response as success — a real bug worth surfacing even though
 * a body-aware client would notice the auth failure.
 */
const TIMEOUT_MS = 8000;

const AUTH_KEYWORDS =
    /"error"|unauthor|forbidden|not\s*allowed|permission\s+denied|requires?\s+auth|auth(?:entication)?\s+required|service\s*(?:role|key)/i;

const SERVER_AUTH_KEYWORDS =
    /unauthor|forbidden|user\.me|401|403|permission\s+denied|requires?\s+auth|auth(?:entication)?\s+required|service\s*(?:role|key)/i;

function detectWrappedStatus(snippet) {
    if (!snippet) return false;
    try {
        const parsed = JSON.parse(snippet);
        if (
            parsed &&
            typeof parsed.statusCode === "number" &&
            parsed.statusCode >= 400 &&
            parsed.statusCode < 600
        ) {
            return true;
        }
    } catch {
        // fall through to regex sniff (snippet may be truncated JSON)
    }
    return /"statusCode"\s*:\s*[45]\d\d/.test(snippet);
}

export async function liveProbeFunction({ baseUrl, functionName }) {
    if (!baseUrl || !functionName) {
        return { verdict: "inconclusive", reason: "missingInputs" };
    }

    const url = `${baseUrl.replace(/\/$/, "")}/api/${functionName}`;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);

    try {
        const res = await fetch(url, {
            method: "POST",
            credentials: "omit",
            headers: { "Content-Type": "application/json" },
            body: "{}",
            signal: ctrl.signal,
        });

        let snippet = "";
        try {
            const text = await res.text();
            snippet = text.slice(0, 240);
        } catch {
            // unreadable body
        }

        const status = res.status;

        if (status >= 200 && status < 300) {
            if (detectWrappedStatus(snippet)) {
                return {
                    verdict: "blocked",
                    status,
                    snippet,
                    warnings: ["wrappedStatus"],
                };
            }
            return {
                verdict: AUTH_KEYWORDS.test(snippet) ? "blocked" : "open",
                status,
                snippet,
            };
        }

        if (status === 401 || status === 403) {
            return { verdict: "blocked", status, snippet };
        }

        if (status >= 500) {
            return {
                verdict: SERVER_AUTH_KEYWORDS.test(snippet)
                    ? "blocked"
                    : "inconclusive",
                status,
                snippet,
            };
        }

        return { verdict: "inconclusive", status, snippet };
    } catch (err) {
        return {
            verdict: "inconclusive",
            reason: err?.name === "AbortError" ? "timeout" : "networkOrCors",
        };
    } finally {
        clearTimeout(timer);
    }
}
